import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '../components/ui/dialog';
import HistorialBibliotecaMatriculada from '../components/HistorialBibliotecaMatriculada';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Search, Eye, Edit, Trash2, AlertCircle, User, MapPin, Briefcase, Save, X, UserPlus, Upload, Users, FileSpreadsheet, CheckCircle, FileText, Check, ArrowUpDown, ArrowUp, ArrowDown, KeyRound, RefreshCw, MessageSquare, UserCheck, Mail, Shield, UserX, RotateCcw, BookOpen } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { fechaToInput, fechaToDisplay } from '../utils/fechaUtils';
import { TITULOS_VIGENTES, TITULOS_ANTERIORES } from '../constants/titulos';
import HistorialCorreosMatriculado from '../components/bandeja/HistorialCorreosMatriculado';
import { ActualizacionFamiliarPerfil } from '../components/actualizacion-familiar/ActualizacionFamiliarPerfil';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const USUARIOS_PAGE_SIZE = 50;

const GENEROS = [
  "Masculino",
  "Femenino", 
  "Feminidad trans/travesti",
  "Varón trans/masculinidad trans",
  "No binarie",
  "Otro"
];

export default function AdminDashboard() {
  const [trabajadores, setTrabajadores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTrabajador, setSelectedTrabajador] = useState(null);
  const [viewMode, setViewMode] = useState('view'); // 'view' or 'edit'
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [suspenderModal, setSuspenderModal] = useState({ open: false, trabajador: null, motivo: '' });
  const [suspendiendo, setSuspendiendo] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('lista');
  
  // Estado para Crear TS (formulario completo)
  const [nuevoTS, setNuevoTS] = useState({
    numero_matricula: '',
    documento: '',
    apellido: '',
    nombre: '',
    fecha_nacimiento: '',
    genero: '',
    titulo: '',
    email: '',
    celular: '',
    domicilio_real: { calle: '', numero: '', piso: '', depto: '', localidad: '', partido: '' },
    domicilio_laboral: { calle: '', numero: '', piso: '', depto: '', localidad: '', partido: '' },
    sin_empleo: false
  });
  const [creando, setCreando] = useState(false);
  const [mensajeCrear, setMensajeCrear] = useState({ tipo: '', texto: '' });
  
  // Estado para Carga Masiva
  const [archivo, setArchivo] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [resultadoCarga, setResultadoCarga] = useState(null);
  
  // Estado para ordenamiento de tabla
  const [sortField, setSortField] = useState(null); // 'numero_matricula', 'apellido', 'nombre', 'email'
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' o 'desc'
  
  // Estado para Usuarios del Portal
  const { token, user: currentUser } = useAuth();
  const [usuariosPortal, setUsuariosPortal] = useState([]);
  const [usuariosMeta, setUsuariosMeta] = useState({ page: 1, total: 0, total_pages: 1, bajas_count: 0 });
  const [usuariosPage, setUsuariosPage] = useState(1);
  const [searchUsuariosDebounced, setSearchUsuariosDebounced] = useState('');
  const [mostrarBajasUsuarios, setMostrarBajasUsuarios] = useState(false);
  const [loadingUsuarios, setLoadingUsuarios] = useState(false);
  const [searchUsuarios, setSearchUsuarios] = useState('');
  const [editUsuario, setEditUsuario] = useState(null);
  const [editUsuarioForm, setEditUsuarioForm] = useState({ email: '', nueva_password: '' });
  const [savingUsuario, setSavingUsuario] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'fecha_creacion', direction: 'desc' });
  
  // Estado para cambio de rol
  const [cambioRolUsuario, setCambioRolUsuario] = useState(null);
  const [nuevoRol, setNuevoRol] = useState('');
  const [savingRol, setSavingRol] = useState(false);

  useEffect(() => {
    fetchTrabajadores();
  }, []);

  // Deep-link: si viene ?matricula=XXX (opcional &ver=1), abrir el modal de detalle.
  // Usado desde la Bandeja Unificada ("Ver ficha completa") y potencialmente desde emails.
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const matriculaParam = searchParams.get('matricula');
    if (!matriculaParam || trabajadores.length === 0) return;
    const found = trabajadores.find(t => String(t.numero_matricula) === String(matriculaParam));
    if (found) {
      setSelectedTrabajador(found);
      setViewMode('view');
    }
  }, [searchParams, trabajadores]);

  const fetchTrabajadores = async () => {
    try {
      const response = await axios.get(`${API_URL}/trabajadores`);
      setTrabajadores(response.data);
    } catch (err) {
      console.error('Error fetching trabajadores:', err);
    } finally {
      setLoading(false);
    }
  };
  
  // === Funciones para Usuarios del Portal ===
  const fetchUsuariosPortal = async () => {
    if (!token) return;
    setLoadingUsuarios(true);
    try {
      const response = await axios.get(`${API_URL}/admin/usuarios-portal`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          page: usuariosPage, page_size: USUARIOS_PAGE_SIZE, q: searchUsuariosDebounced,
          mostrar_bajas: mostrarBajasUsuarios, sort: sortConfig.key, direction: sortConfig.direction,
        },
      });
      const { items, ...meta } = response.data;
      setUsuariosPortal(items);
      setUsuariosMeta(meta);
    } catch (err) {
      console.error('Error fetching usuarios portal:', err);
    } finally {
      setLoadingUsuarios(false);
    }
  };
  
  useEffect(() => {
    const t = setTimeout(() => { setSearchUsuariosDebounced(searchUsuarios.trim()); setUsuariosPage(1); }, 350);
    return () => clearTimeout(t);
  }, [searchUsuarios]);

  // Paginación, búsqueda, orden y bajas se resuelven en el servidor
  useEffect(() => {
    if (activeTab === 'usuarios') fetchUsuariosPortal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, usuariosPage, searchUsuariosDebounced, mostrarBajasUsuarios, sortConfig, token]);

  const handleSortUsuarios = (key) => {
    setUsuariosPage(1);
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const usuariosPortalFiltrados = usuariosPortal;
  const usuariosBajasCount = usuariosMeta.bajas_count || 0;

  const handleEditUsuario = (usuario) => {
    setEditUsuario(usuario);
    setEditUsuarioForm({ email: usuario.email, nueva_password: '' });
  };
  
  const handleSaveUsuario = async () => {
    if (!editUsuario) return;
    setSavingUsuario(true);
    try {
      await axios.put(`${API_URL}/admin/usuarios-portal/${editUsuario.id}`, editUsuarioForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEditUsuario(null);
      fetchUsuariosPortal();
      alert('Usuario actualizado correctamente');
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al actualizar usuario');
    } finally {
      setSavingUsuario(false);
    }
  };
  
  const handleDeleteUsuario = async (usuario) => {
    const nombreCompleto = usuario.trabajador_info 
      ? `${usuario.trabajador_info.apellido}, ${usuario.trabajador_info.nombre}`
      : usuario.email;
    
    if (!window.confirm(`¿Eliminar la cuenta de ${nombreCompleto}?\n\nEsto solo elimina el acceso al portal, los datos del matriculado se mantienen.`)) {
      return;
    }
    
    try {
      await axios.delete(`${API_URL}/admin/usuarios-portal/${usuario.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchUsuariosPortal();
      alert('Cuenta eliminada correctamente');
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al eliminar usuario');
    }
  };

  const handleExportarUsuarios = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/usuarios-portal/exportar`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const { usuarios, fecha_exportacion } = response.data;
      
      if (!usuarios || usuarios.length === 0) {
        alert('No hay usuarios para exportar');
        return;
      }
      
      // Crear CSV
      const headers = Object.keys(usuarios[0]);
      const csvContent = [
        headers.join(','),
        ...usuarios.map(u => 
          headers.map(h => {
            let val = u[h];
            if (val === null || val === undefined) val = '';
            if (typeof val === 'boolean') val = val ? 'Sí' : 'No';
            // Escapar comillas y envolver en comillas si contiene coma
            val = String(val).replace(/"/g, '""');
            if (val.includes(',') || val.includes('"') || val.includes('\n')) {
              val = `"${val}"`;
            }
            return val;
          }).join(',')
        )
      ].join('\n');
      
      // Descargar
      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `usuarios_portal_${new Date().toISOString().slice(0,10)}.csv`;
      link.click();
      
    } catch (err) {
      console.error('Error exportando usuarios:', err);
      alert(err.response?.data?.detail || 'Error al exportar usuarios');
    }
  };
  
  const handleCambiarRol = async () => {
    if (!cambioRolUsuario || !nuevoRol) return;
    
    setSavingRol(true);
    try {
      await axios.put(`${API_URL}/admin/usuarios/${cambioRolUsuario.id}/rol`, 
        { rol: nuevoRol },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCambioRolUsuario(null);
      setNuevoRol('');
      fetchUsuariosPortal();
      alert(`Rol actualizado a ${nuevoRol}`);
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al cambiar rol');
    } finally {
      setSavingRol(false);
    }
  };
  
  const abrirModalCambioRol = (usuario) => {
    setCambioRolUsuario(usuario);
    setNuevoRol(usuario.rol || 'trabajador_social');
  };
  
  // === Funciones auxiliares para Crear TS ===
  const updateNuevoTS = (field, value) => {
    setNuevoTS(prev => ({ ...prev, [field]: value }));
  };

  const updateNuevoDomicilio = (tipo, field, value) => {
    setNuevoTS(prev => ({
      ...prev,
      [tipo]: { ...prev[tipo], [field]: value }
    }));
  };

  // === CREAR TS ===
  const handleCrearTS = async (e) => {
    e.preventDefault();
    setCreando(true);
    setMensajeCrear({ tipo: '', texto: '' });
    
    try {
      const token = localStorage.getItem('token');
      // Convertir fecha al formato dd/mm/yyyy para guardar
      const dataToSave = {
        ...nuevoTS,
        fecha_nacimiento: fechaToDisplay(nuevoTS.fecha_nacimiento)
      };
      await axios.post(`${API_URL}/trabajadores`, dataToSave, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setMensajeCrear({ tipo: 'success', texto: 'Trabajador/a Social creado/a exitosamente' });
      setNuevoTS({
        numero_matricula: '',
        documento: '',
        apellido: '',
        nombre: '',
        fecha_nacimiento: '',
        genero: '',
        titulo: '',
        email: '',
        celular: '',
        domicilio_real: { calle: '', numero: '', piso: '', depto: '', localidad: '', partido: '' },
        domicilio_laboral: { calle: '', numero: '', piso: '', depto: '', localidad: '', partido: '' },
        sin_empleo: false
      });
      fetchTrabajadores();
      
      // Cambiar a pestaña de lista después de crear
      setTimeout(() => setActiveTab('lista'), 1500);
    } catch (err) {
      setMensajeCrear({ 
        tipo: 'error', 
        texto: err.response?.data?.detail || 'Error al crear el trabajador/a social'
      });
    } finally {
      setCreando(false);
    }
  };
  
  // === CARGA MASIVA ===
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.name.endsWith('.csv')) {
      setArchivo(selectedFile);
    } else {
      setResultadoCarga({ error: true, mensaje: 'Por favor selecciona un archivo CSV' });
      setArchivo(null);
    }
  };

  const handleCargaMasiva = async (e) => {
    e.preventDefault();
    if (!archivo) return;
    
    setCargando(true);
    setResultadoCarga(null);
    
    try {
      const formData = new FormData();
      formData.append('file', archivo);
      
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/trabajadores/carga-masiva`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setResultadoCarga(response.data);
      setArchivo(null);
      fetchTrabajadores();
    } catch (err) {
      setResultadoCarga({ 
        error: true, 
        mensaje: err.response?.data?.detail || 'Error al procesar el archivo'
      });
    } finally {
      setCargando(false);
    }
  };

  const resetCargaMasiva = () => {
    setArchivo(null);
    setResultadoCarga(null);
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_URL}/trabajadores/${id}`);
      setTrabajadores(prev => prev.filter(t => t.id !== id));
      setDeleteConfirm(null);
    } catch (err) {
      console.error('Error deleting:', err);
    }
  };

  const handleView = (trabajador) => {
    setSelectedTrabajador(trabajador);
    setViewMode('view');
    setEditForm(null);
  };

  const handleEdit = (trabajador) => {
    setSelectedTrabajador(trabajador);
    setViewMode('edit');
    setEditForm({
      ...trabajador,
      fecha_nacimiento: fechaToInput(trabajador.fecha_nacimiento),
      domicilio_real: trabajador.domicilio_real || { calle: '', numero: '', piso: '', depto: '', localidad: '' },
      domicilio_laboral: trabajador.domicilio_laboral || { calle: '', numero: '', piso: '', depto: '', localidad: '' }
    });
  };

  const handleSuspender = async () => {
    const { trabajador, motivo } = suspenderModal;
    if (!trabajador || !motivo || motivo.trim().length < 3) {
      alert('El motivo es obligatorio (mínimo 3 caracteres).');
      return;
    }
    if (!window.confirm(`Confirmar la BAJA MANUAL de ${trabajador.apellido}, ${trabajador.nombre} (mat ${trabajador.numero_matricula}).\n\nEsto lo saca de la lista de activos, desactiva su usuario del portal, y lo excluye del mailing y del envío del día 10.\n\n¿Continuar?`)) return;
    try {
      setSuspendiendo(true);
      await axios.post(
        `${API_URL}/admin/matriculades/${trabajador.id}/marcar-baja-manual`,
        { motivo: motivo.trim() },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setSuspenderModal({ open: false, trabajador: null, motivo: '' });
      setSelectedTrabajador(null);
      await fetchTrabajadores();
    } catch (e) {
      alert('Error: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSuspendiendo(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!editForm) return;
    setError('');
    setSaving(true);

    try {
      // Convertir fecha al formato dd/mm/yyyy para guardar
      const dataToSave = {
        ...editForm,
        fecha_nacimiento: fechaToDisplay(editForm.fecha_nacimiento)
      };
      await axios.put(`${API_URL}/trabajadores/${editForm.id}`, dataToSave);
      await fetchTrabajadores();
      setSelectedTrabajador(null);
      setEditForm(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const updateEditForm = (field, value) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
  };

  const updateEditDomicilio = (tipo, field, value) => {
    setEditForm(prev => ({
      ...prev,
      [tipo]: { ...prev[tipo], [field]: value }
    }));
  };

  const filteredTrabajadores = trabajadores.filter(t => {
    const search = searchTerm.toLowerCase();
    return (
      t.numero_matricula?.toLowerCase().includes(search) ||
      t.apellido?.toLowerCase().includes(search) ||
      t.nombre?.toLowerCase().includes(search) ||
      t.email?.toLowerCase().includes(search)
    );
  });

  // Ordenar la lista filtrada
  const sortedTrabajadores = useMemo(() => {
    if (!sortField) return filteredTrabajadores;
    
    return [...filteredTrabajadores].sort((a, b) => {
      const aValue = a[sortField] || '';
      const bValue = b[sortField] || '';
      
      // Para matrícula, ordenar numéricamente
      if (sortField === 'numero_matricula') {
        const aNum = parseInt(aValue, 10) || 0;
        const bNum = parseInt(bValue, 10) || 0;
        return sortDirection === 'asc' ? aNum - bNum : bNum - aNum;
      }
      
      // Para otros campos, ordenar alfabéticamente
      const aLower = aValue.toLowerCase();
      const bLower = bValue.toLowerCase();
      
      if (aLower < bLower) return sortDirection === 'asc' ? -1 : 1;
      if (aLower > bLower) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredTrabajadores, sortField, sortDirection]);

  // Función para manejar click en header
  const handleSort = (field) => {
    if (sortField === field) {
      // Si es el mismo campo, alternar dirección
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      // Nuevo campo, empezar ascendente
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Componente para header ordenable
  const SortableHeader = ({ field, children }) => (
    <th 
      onClick={() => handleSort(field)}
      className="cursor-pointer hover:bg-gray-100 select-none transition-colors"
      data-testid={`sort-${field}`}
    >
      <div className="flex items-center gap-1">
        {children}
        {sortField === field ? (
          sortDirection === 'asc' ? (
            <ArrowUp className="w-4 h-4 text-violet-600" />
          ) : (
            <ArrowDown className="w-4 h-4 text-violet-600" />
          )
        ) : (
          <ArrowUpDown className="w-4 h-4 text-gray-400" />
        )}
      </div>
    </th>
  );

  return (
    <div>
      <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6">Matriculades</h1>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="overflow-x-auto pb-2 -mx-4 px-4 md:mx-0 md:px-0">
          <TabsList className="mb-6 inline-flex min-w-max">
            <TabsTrigger value="lista" className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              Lista
            </TabsTrigger>
            <TabsTrigger value="usuarios" className="flex items-center gap-2">
              <KeyRound className="w-4 h-4" />
              Usuarios Portal
            </TabsTrigger>
            <TabsTrigger value="interacciones" className="flex items-center gap-2">
              <MessageSquare className="w-4 h-4" />
              TS sin usuario
            </TabsTrigger>
            <TabsTrigger value="crear" className="flex items-center gap-2">
              <UserPlus className="w-4 h-4" />
              Crear TS
            </TabsTrigger>
            <TabsTrigger value="carga" className="flex items-center gap-2">
              <Upload className="w-4 h-4" />
              Carga Masiva
            </TabsTrigger>
            <TabsTrigger value="bajas" className="flex items-center gap-2" data-testid="tab-bajas">
              <UserX className="w-4 h-4" />
              Bajas / Suspendidos
            </TabsTrigger>
          </TabsList>
        </div>

        {/* Tab Lista */}
        <TabsContent value="lista">
          {/* Conteo Total */}
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-violet-100 rounded-lg">
                <Users className="h-5 w-5 text-violet-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{trabajadores.length}</p>
                <p className="text-sm text-gray-500">Matriculades en el sistema</p>
              </div>
            </div>
            {searchTerm && (
              <p className="text-sm text-gray-500">
                Mostrando {sortedTrabajadores.length} de {trabajadores.length}
              </p>
            )}
          </div>

          {/* Search */}
          <Card className="mb-6">
            <CardContent className="pt-6">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Buscar por matrícula, nombre, apellido o email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-trabajadores"
                />
              </div>
            </CardContent>
          </Card>

          {/* Table */}
          <Card>
            <CardContent className="p-0">
              {loading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                </div>
              ) : sortedTrabajadores.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  No se encontraron trabajadores
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <SortableHeader field="numero_matricula">Matrícula</SortableHeader>
                        <SortableHeader field="apellido">Apellido</SortableHeader>
                        <SortableHeader field="nombre">Nombre</SortableHeader>
                        <SortableHeader field="email">Email</SortableHeader>
                        <th>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedTrabajadores.map(t => (
                        <tr key={t.id} data-testid={`row-${t.numero_matricula}`}>
                          <td className="font-medium">{t.numero_matricula}</td>
                          <td>{t.apellido}</td>
                          <td>{t.nombre}</td>
                          <td className="text-gray-500">{t.email}</td>
                          <td>
                            <div className="flex gap-2">
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => { setSelectedTrabajador(t); setViewMode('view'); }}
                                data-testid={`view-${t.numero_matricula}`}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => { 
                                  setSelectedTrabajador(t); 
                                  setViewMode('edit');
                                  setEditForm({
                                    ...t,
                                    fecha_nacimiento: fechaToInput(t.fecha_nacimiento),
                                  });
                                }}
                                data-testid={`edit-${t.numero_matricula}`}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="destructive"
                                onClick={() => setDeleteConfirm(t)}
                                data-testid={`delete-${t.numero_matricula}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab Usuarios del Portal */}
        <TabsContent value="usuarios">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <KeyRound className="w-5 h-5 text-violet-600" />
                    Usuarios del Portal
                  </CardTitle>
                  <CardDescription>
                    Matriculados que crearon cuenta para acceder al portal de servicios
                  </CardDescription>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm text-gray-500" data-testid="usuarios-portal-total">{usuariosMeta.total} usuarios</span>
                  {usuariosBajasCount > 0 && (
                    <button
                      onClick={() => { setMostrarBajasUsuarios(v => !v); setUsuariosPage(1); }}
                      className={`text-xs px-2 py-1 rounded border ${mostrarBajasUsuarios ? 'bg-red-100 text-red-800 border-red-300' : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'}`}
                      data-testid="toggle-mostrar-bajas-usuarios"
                    >
                      {mostrarBajasUsuarios ? 'Ocultar' : 'Mostrar'} {usuariosBajasCount} bajas
                    </button>
                  )}
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleExportarUsuarios}
                    title="Exportar usuarios a CSV"
                  >
                    <FileSpreadsheet className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={fetchUsuariosPortal} disabled={loadingUsuarios}>
                    <RefreshCw className={`w-4 h-4 ${loadingUsuarios ? 'animate-spin' : ''}`} />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Buscador */}
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Buscar por email, nombre, matrícula o DNI..."
                  value={searchUsuarios}
                  onChange={(e) => setSearchUsuarios(e.target.value)}
                  className="pl-10"
                />
              </div>

              {loadingUsuarios ? (
                <div className="text-center py-8">
                  <RefreshCw className="w-8 h-8 mx-auto animate-spin text-gray-400" />
                  <p className="text-gray-500 mt-2">Cargando usuarios...</p>
                </div>
              ) : usuariosPortalFiltrados.length === 0 ? (
                <div className="text-center py-8">
                  <KeyRound className="w-12 h-12 mx-auto text-gray-300" />
                  <p className="text-gray-500 mt-2">
                    {searchUsuarios ? 'No se encontraron usuarios' : 'No hay usuarios registrados'}
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th 
                          className="text-left p-3 font-medium cursor-pointer hover:bg-gray-100 select-none"
                          onClick={() => handleSortUsuarios('apellido')}
                        >
                          Apellido {sortConfig.key === 'apellido' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                        </th>
                        <th 
                          className="text-left p-3 font-medium cursor-pointer hover:bg-gray-100 select-none"
                          onClick={() => handleSortUsuarios('nombre')}
                        >
                          Nombre {sortConfig.key === 'nombre' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                        </th>
                        <th 
                          className="text-left p-3 font-medium cursor-pointer hover:bg-gray-100 select-none"
                          onClick={() => handleSortUsuarios('email')}
                        >
                          Email {sortConfig.key === 'email' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                        </th>
                        <th 
                          className="text-left p-3 font-medium cursor-pointer hover:bg-gray-100 select-none"
                          onClick={() => handleSortUsuarios('matricula')}
                        >
                          Matrícula {sortConfig.key === 'matricula' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                        </th>
                        <th 
                          className="text-left p-3 font-medium cursor-pointer hover:bg-gray-100 select-none"
                          onClick={() => handleSortUsuarios('documento')}
                        >
                          DNI {sortConfig.key === 'documento' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                        </th>
                        <th 
                          className="text-left p-3 font-medium cursor-pointer hover:bg-gray-100 select-none"
                          onClick={() => handleSortUsuarios('fecha_creacion')}
                        >
                          Alta Cuenta Portal {sortConfig.key === 'fecha_creacion' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                        </th>
                        <th 
                          className="text-left p-3 font-medium cursor-pointer hover:bg-gray-100 select-none"
                          onClick={() => handleSortUsuarios('updated_at')}
                        >
                          Últ. Cambio en Padrón {sortConfig.key === 'updated_at' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                        </th>
                        <th className="text-left p-3 font-medium">Rol</th>
                        <th className="text-left p-3 font-medium">Estado Padrón</th>
                        <th className="text-center p-3 font-medium">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {usuariosPortalFiltrados.map(usuario => {
                        // Fecha "Últ. Cambio en Padrón" ahora mapea a `usuario.updated_at`
                        // (cambio en el usuario del portal), no al trabajador social.
                        const fechaActualizacion = usuario.updated_at;
                        const fechaCreacion = usuario.fecha_creacion;
                        // El "fantasma" 10/02/2026 solo aplicaba a la data del trabajador social;
                        // con updated_at ya no se necesita ese filtro.
                        const esFechaFantasma = false;
                        
                        return (
                          <tr key={usuario.id} className="border-b hover:bg-gray-50">
                            <td className="p-3 font-medium">
                              {usuario.trabajador_info?.apellido || <span className="text-gray-400 italic">-</span>}
                            </td>
                            <td className="p-3">
                              {usuario.trabajador_info?.nombre || <span className="text-gray-400 italic">-</span>}
                            </td>
                            <td className="p-3">{usuario.email}</td>
                            <td className="p-3">{usuario.trabajador_info?.numero_matricula || '-'}</td>
                            <td className="p-3">{usuario.trabajador_info?.documento || '-'}</td>
                            <td className="p-3 text-gray-500">
                              {fechaCreacion ? new Date(fechaCreacion).toLocaleDateString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires' }) : '-'}
                            </td>
                            <td className="p-3 text-gray-500">
                              {fechaActualizacion && !esFechaFantasma
                                ? new Date(fechaActualizacion).toLocaleString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', day: '2-digit',
                                    month: '2-digit', 
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })
                                : <span className="text-gray-400 italic">-</span>
                              }
                            </td>
                            <td className="p-3">
                              {usuario.rol === 'superadmin' ? (
                                <Badge className="bg-purple-100 text-purple-800">Superadmin</Badge>
                              ) : usuario.rol === 'admin' || usuario.rol === 'administracion' ? (
                                <Badge className="bg-blue-100 text-blue-800">Admin</Badge>
                              ) : (
                                <Badge variant="outline">Usuario</Badge>
                              )}
                            </td>
                            <td className="p-3">
                              {(() => {
                                const est = usuario.trabajador_info?.estado_padron;
                                if (est === 'baja_detectada') {
                                  return <Badge className="bg-red-100 text-red-800" title={usuario.trabajador_info?.motivo_baja || 'Baja detectada por ANTUAR'} data-testid={`estado-padron-${usuario.id}`}>Baja detectada</Badge>;
                                }
                                if (est === 'baja_manual') {
                                  return <Badge className="bg-red-100 text-red-800" title={usuario.trabajador_info?.motivo_baja || 'Baja manual'} data-testid={`estado-padron-${usuario.id}`}>Baja manual</Badge>;
                                }
                                if (est === 'reactivada_pendiente_confirmacion') {
                                  return <Badge className="bg-amber-100 text-amber-800" data-testid={`estado-padron-${usuario.id}`}>Reactivación pendiente</Badge>;
                                }
                                return <Badge className="bg-green-100 text-green-800" data-testid={`estado-padron-${usuario.id}`}>Activa</Badge>;
                              })()}
                            </td>
                            <td className="p-3">
                            <div className="flex items-center justify-center gap-1">
                              {(currentUser?.rol === 'superadmin' || currentUser?.rol === 'admin') && usuario.email !== currentUser?.email && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => abrirModalCambioRol(usuario)}
                                  title="Cambiar permisos"
                                >
                                  <Shield className="w-4 h-4 text-purple-600" />
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEditUsuario(usuario)}
                                title="Editar usuario"
                              >
                                <Edit className="w-4 h-4 text-blue-600" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteUsuario(usuario)}
                                title="Eliminar cuenta"
                              >
                                <Trash2 className="w-4 h-4 text-red-600" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              {usuariosMeta.total_pages > 1 && (
                <div className="flex items-center justify-between mt-4 text-sm" data-testid="usuarios-portal-paginacion">
                  <span className="text-gray-500">
                    Página {usuariosMeta.page} de {usuariosMeta.total_pages} · mostrando {usuariosPortal.length} de {usuariosMeta.total}
                  </span>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" disabled={loadingUsuarios || usuariosMeta.page <= 1}
                      onClick={() => setUsuariosPage(p => Math.max(1, p - 1))} data-testid="usuarios-portal-prev">Anterior</Button>
                    <Button variant="outline" size="sm" disabled={loadingUsuarios || usuariosMeta.page >= usuariosMeta.total_pages}
                      onClick={() => setUsuariosPage(p => p + 1)} data-testid="usuarios-portal-next">Siguiente</Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Modal Editar Usuario */}
          <Dialog open={!!editUsuario} onOpenChange={() => setEditUsuario(null)}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Editar Usuario del Portal</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                {/* Banner si el usuario está de baja */}
                {(editUsuario?.trabajador_info?.estado_padron === 'baja_detectada' ||
                  editUsuario?.trabajador_info?.estado_padron === 'baja_manual') && (
                  <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-300 rounded-lg" data-testid="banner-usuario-baja">
                    <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm">
                      <p className="font-semibold text-red-800">
                        Este usuario ha sido dado de baja en el padrón según el último control de ANTUAR.
                      </p>
                      {editUsuario.trabajador_info?.motivo_baja && (
                        <p className="text-red-700 mt-1">Motivo: {editUsuario.trabajador_info.motivo_baja}</p>
                      )}
                      <p className="text-red-700 mt-1 text-xs">
                        Podés revertirlo desde la pestaña <strong>Bajas / Suspendidos</strong> con el botón "Reactivar".
                      </p>
                    </div>
                  </div>
                )}

                {editUsuario?.trabajador_info && (
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="font-medium">
                      {editUsuario.trabajador_info.apellido}, {editUsuario.trabajador_info.nombre}
                    </p>
                    <p className="text-sm text-gray-500">
                      Mat. {editUsuario.trabajador_info.numero_matricula} | DNI {editUsuario.trabajador_info.documento}
                    </p>
                  </div>
                )}
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={editUsuarioForm.email}
                    onChange={(e) => setEditUsuarioForm(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="Email del usuario"
                  />
                </div>
                <div>
                  <Label>Nueva Contraseña (dejar vacío para no cambiar)</Label>
                  <Input
                    type="password"
                    value={editUsuarioForm.nueva_password}
                    onChange={(e) => setEditUsuarioForm(prev => ({ ...prev, nueva_password: e.target.value }))}
                    placeholder="Nueva contraseña"
                  />
                  <p className="text-xs text-gray-500 mt-1">Mínimo 6 caracteres</p>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditUsuario(null)}>
                  Cancelar
                </Button>
                <Button onClick={handleSaveUsuario} disabled={savingUsuario}>
                  {savingUsuario ? 'Guardando...' : 'Guardar Cambios'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          
          {/* Modal Cambiar Rol */}
          <Dialog open={!!cambioRolUsuario} onOpenChange={() => setCambioRolUsuario(null)}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-purple-600" />
                  Cambiar Permisos
                </DialogTitle>
              </DialogHeader>
              
              {cambioRolUsuario && (
                <div className="space-y-4">
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="font-medium">{cambioRolUsuario.trabajador_info?.nombre} {cambioRolUsuario.trabajador_info?.apellido}</p>
                    <p className="text-sm text-gray-600">{cambioRolUsuario.email}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      Rol actual: <span className="font-medium">{cambioRolUsuario.rol || 'trabajador_social'}</span>
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">Nuevo rol</label>
                    <select
                      value={nuevoRol}
                      onChange={(e) => setNuevoRol(e.target.value)}
                      className="w-full p-2 border rounded-lg"
                    >
                      <option value="trabajador_social">Usuario (trabajador_social)</option>
                      <option value="admin">Administrador (admin)</option>
                      <option value="superadmin">Super Administrador (superadmin)</option>
                    </select>
                    
                    <div className="mt-3 text-sm text-gray-600">
                      <p className="font-medium mb-1">Permisos:</p>
                      <ul className="list-disc list-inside space-y-1 text-xs">
                        {nuevoRol === 'trabajador_social' && (
                          <>
                            <li>Acceso al portal de usuario</li>
                            <li>Ver y editar su perfil</li>
                            <li>Inscribirse a cursos y eventos</li>
                          </>
                        )}
                        {nuevoRol === 'admin' && (
                          <>
                            <li>Todo lo anterior +</li>
                            <li>Acceso al panel de administración</li>
                            <li>Gestionar matriculados, cursos, eventos</li>
                            <li>Ver estadísticas</li>
                          </>
                        )}
                        {nuevoRol === 'superadmin' && (
                          <>
                            <li>Todo lo anterior +</li>
                            <li>Cambiar permisos de otros usuarios</li>
                            <li>Eliminar administradores</li>
                            <li>Configuración del sistema</li>
                          </>
                        )}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setCambioRolUsuario(null)}>
                  Cancelar
                </Button>
                <Button 
                  onClick={handleCambiarRol}
                  disabled={savingRol || nuevoRol === cambioRolUsuario?.rol}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  {savingRol ? 'Guardando...' : 'Cambiar Rol'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* Tab TS sin usuario */}
        <TabsContent value="interacciones">
          <InteraccionesWhatsAppTab token={token} />
        </TabsContent>

        {/* Tab Crear TS - Formulario Completo */}
        <TabsContent value="crear">
          <Card>
            <CardContent className="pt-6">
              <form onSubmit={handleCrearTS} className="space-y-8">
                {mensajeCrear.tipo === 'error' && (
                  <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                    <AlertCircle className="w-4 h-4" />
                    {mensajeCrear.texto}
                  </div>
                )}

                {mensajeCrear.tipo === 'success' && (
                  <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-emerald-700 text-sm">
                    <Check className="w-4 h-4" />
                    {mensajeCrear.texto}
                  </div>
                )}

                {/* Datos Básicos */}
                <div>
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <User className="w-5 h-5 text-primary" />
                    Datos Básicos
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Número de Matrícula *</Label>
                      <Input 
                        value={nuevoTS.numero_matricula} 
                        onChange={(e) => updateNuevoTS('numero_matricula', e.target.value)} 
                        required 
                        data-testid="crear-matricula"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Documento *</Label>
                      <Input 
                        value={nuevoTS.documento} 
                        onChange={(e) => updateNuevoTS('documento', e.target.value)} 
                        required 
                        data-testid="crear-documento"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Apellido *</Label>
                      <Input 
                        value={nuevoTS.apellido} 
                        onChange={(e) => updateNuevoTS('apellido', e.target.value)} 
                        required 
                        data-testid="crear-apellido"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Nombre *</Label>
                      <Input 
                        value={nuevoTS.nombre} 
                        onChange={(e) => updateNuevoTS('nombre', e.target.value)} 
                        required 
                        data-testid="crear-nombre"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Fecha de Nacimiento *</Label>
                      <Input 
                        type="date"
                        value={nuevoTS.fecha_nacimiento} 
                        onChange={(e) => updateNuevoTS('fecha_nacimiento', e.target.value)} 
                        required 
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Género</Label>
                      <select 
                        value={nuevoTS.genero} 
                        onChange={(e) => updateNuevoTS('genero', e.target.value)}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      >
                        <option value="">Seleccionar</option>
                        {GENEROS.map(g => <option key={g} value={g}>{g}</option>)}
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label>Título *</Label>
                      <select 
                        value={nuevoTS.titulo} 
                        onChange={(e) => updateNuevoTS('titulo', e.target.value)}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                        required
                      >
                        <option value="">Seleccionar</option>
                        <optgroup label="Titulaciones vigentes">
                          {TITULOS_VIGENTES.map(t => <option key={t} value={t}>{t}</option>)}
                        </optgroup>
                        <optgroup label="Titulaciones anteriores">
                          {TITULOS_ANTERIORES.map(t => <option key={t} value={t}>{t}</option>)}
                        </optgroup>
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label>Email *</Label>
                      <Input 
                        type="email" 
                        value={nuevoTS.email} 
                        onChange={(e) => updateNuevoTS('email', e.target.value)} 
                        required 
                        data-testid="crear-email"
                      />
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <Label>Celular *</Label>
                      <Input 
                        value={nuevoTS.celular} 
                        onChange={(e) => updateNuevoTS('celular', e.target.value)} 
                        required 
                      />
                    </div>
                  </div>
                </div>

                {/* Domicilio Real */}
                <div>
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-primary" />
                    Domicilio Real
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="col-span-2 space-y-2">
                      <Label>Calle *</Label>
                      <Input value={nuevoTS.domicilio_real.calle} onChange={(e) => updateNuevoDomicilio('domicilio_real', 'calle', e.target.value)} required />
                    </div>
                    <div className="space-y-2">
                      <Label>Número *</Label>
                      <Input value={nuevoTS.domicilio_real.numero} onChange={(e) => updateNuevoDomicilio('domicilio_real', 'numero', e.target.value)} required />
                    </div>
                    <div className="space-y-2">
                      <Label>Piso</Label>
                      <Input value={nuevoTS.domicilio_real.piso} onChange={(e) => updateNuevoDomicilio('domicilio_real', 'piso', e.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <Label>Depto</Label>
                      <Input value={nuevoTS.domicilio_real.depto} onChange={(e) => updateNuevoDomicilio('domicilio_real', 'depto', e.target.value)} />
                    </div>
                    <div className="col-span-2 space-y-2">
                      <Label>Localidad *</Label>
                      <Input value={nuevoTS.domicilio_real.localidad} onChange={(e) => updateNuevoDomicilio('domicilio_real', 'localidad', e.target.value)} required />
                    </div>
                    <div className="col-span-2 space-y-2">
                      <Label>Partido/Municipio</Label>
                      <Input value={nuevoTS.domicilio_real.partido} onChange={(e) => updateNuevoDomicilio('domicilio_real', 'partido', e.target.value)} placeholder="Ej: Lomas de Zamora" />
                    </div>
                  </div>
                </div>

                {/* Domicilio Laboral */}
                <div>
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Briefcase className="w-5 h-5 text-primary" />
                    Domicilio Laboral
                  </h3>
                  
                  {/* Checkbox sin empleo */}
                  <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={nuevoTS.sin_empleo}
                        onChange={(e) => updateNuevoTS('sin_empleo', e.target.checked)}
                        className="w-5 h-5 rounded border-amber-400 text-amber-600 focus:ring-amber-500"
                        data-testid="sin-empleo-checkbox"
                      />
                      <span className="text-amber-800 font-medium">Se encuentra actualmente sin empleo</span>
                    </label>
                  </div>

                  {!nuevoTS.sin_empleo && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="col-span-2 space-y-2">
                        <Label>Calle *</Label>
                        <Input value={nuevoTS.domicilio_laboral.calle} onChange={(e) => updateNuevoDomicilio('domicilio_laboral', 'calle', e.target.value)} required={!nuevoTS.sin_empleo} />
                      </div>
                      <div className="space-y-2">
                        <Label>Número *</Label>
                        <Input value={nuevoTS.domicilio_laboral.numero} onChange={(e) => updateNuevoDomicilio('domicilio_laboral', 'numero', e.target.value)} required={!nuevoTS.sin_empleo} />
                      </div>
                      <div className="space-y-2">
                        <Label>Piso</Label>
                        <Input value={nuevoTS.domicilio_laboral.piso} onChange={(e) => updateNuevoDomicilio('domicilio_laboral', 'piso', e.target.value)} />
                      </div>
                      <div className="space-y-2">
                        <Label>Depto</Label>
                        <Input value={nuevoTS.domicilio_laboral.depto} onChange={(e) => updateNuevoDomicilio('domicilio_laboral', 'depto', e.target.value)} />
                      </div>
                      <div className="col-span-2 space-y-2">
                        <Label>Localidad *</Label>
                        <Input value={nuevoTS.domicilio_laboral.localidad} onChange={(e) => updateNuevoDomicilio('domicilio_laboral', 'localidad', e.target.value)} required={!nuevoTS.sin_empleo} />
                      </div>
                      <div className="col-span-2 space-y-2">
                        <Label>Partido/Municipio</Label>
                        <Input value={nuevoTS.domicilio_laboral.partido} onChange={(e) => updateNuevoDomicilio('domicilio_laboral', 'partido', e.target.value)} placeholder="Ej: Lomas de Zamora" />
                      </div>
                    </div>
                  )}

                  {nuevoTS.sin_empleo && (
                    <p className="text-sm text-gray-500 italic">Los campos de domicilio laboral no son requeridos cuando la persona no tiene empleo actualmente.</p>
                  )}
                </div>

                <div className="flex gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setActiveTab('lista')} className="flex-1">
                    Cancelar
                  </Button>
                  <Button type="submit" className="flex-1" disabled={creando} data-testid="crear-submit-btn">
                    <Save className="w-4 h-4 mr-2" />
                    {creando ? 'Guardando...' : 'Guardar Datos'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab Carga Masiva - Completo con instrucciones */}
        <TabsContent value="carga">
          <div className="grid gap-6">
            {/* Instructions */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Formato del archivo CSV
                </CardTitle>
                <CardDescription>
                  El archivo debe contener las siguientes columnas (la primera fila debe ser el encabezado):
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="bg-gray-50 rounded-lg p-4 font-mono text-sm overflow-x-auto">
                  <p className="text-gray-600">
                    numero_matricula, documento, apellido, nombre, fecha_nacimiento, genero, titulo, 
                    email, celular, domicilio_real_calle, domicilio_real_numero, domicilio_real_piso, 
                    domicilio_real_depto, domicilio_real_localidad, domicilio_real_partido,
                    domicilio_laboral_calle, domicilio_laboral_numero, domicilio_laboral_piso,
                    domicilio_laboral_depto, domicilio_laboral_localidad, domicilio_laboral_partido, sin_empleo
                  </p>
                </div>
                <div className="mt-4 text-sm text-gray-500 space-y-1">
                  <p><strong>Campos requeridos:</strong> numero_matricula, documento</p>
                  <p><strong>Formato de fecha:</strong> dd/mm/aaaa</p>
                  <p><strong>Títulos vigentes:</strong> Lic. en Trabajo Social, Lic. en Servicio Social, Tec. en Minoridad y Familia, Trabajador Social (nivel terciario), Doctor en Trabajo Social</p>
                  <p><strong>Títulos anteriores:</strong> Asistente Social, Visitadore de Higiene, Visitadore de Salud Publica, Asist. Soc. Criminologico, Asistente Escolar, Lic. en Desarrollo Social</p>
                  <p><strong>Sin empleo:</strong> Si = 1, No = 0 (vacío)</p>
                  <p><strong>Partido:</strong> Nombre del partido/municipio (ej: Lomas de Zamora, Quilmes, CABA)</p>
                </div>
              </CardContent>
            </Card>

            {/* Upload */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Upload className="w-5 h-5" />
                  Subir Archivo
                </CardTitle>
              </CardHeader>
              <CardContent>
                {!resultadoCarga ? (
                  <form onSubmit={handleCargaMasiva} className="space-y-4">
                    <div className="border-2 border-dashed border-gray-200 rounded-lg p-8 text-center">
                      <Input
                        type="file"
                        accept=".csv"
                        onChange={handleFileChange}
                        className="hidden"
                        id="csv-upload"
                        data-testid="csv-file-input"
                      />
                      <label htmlFor="csv-upload" className="cursor-pointer">
                        <Upload className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                        <p className="text-gray-600">
                          {archivo ? (
                            <span className="flex items-center justify-center gap-2">
                              <FileText className="w-4 h-4" />
                              {archivo.name}
                            </span>
                          ) : (
                            'Hacé clic para seleccionar un archivo CSV'
                          )}
                        </p>
                      </label>
                    </div>

                    <Button 
                      type="submit"
                      className="w-full" 
                      disabled={!archivo || cargando}
                      data-testid="upload-csv-btn"
                    >
                      {cargando ? 'Procesando...' : 'Subir y Procesar'}
                    </Button>
                  </form>
                ) : (
                  <div className="space-y-4">
                    {resultadoCarga.error ? (
                      <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                        <AlertCircle className="w-4 h-4" />
                        {resultadoCarga.mensaje}
                      </div>
                    ) : (
                      <>
                        {/* Results Summary */}
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                          <div className="bg-gray-50 rounded-lg p-4 text-center">
                            <p className="text-2xl font-bold text-gray-900">{resultadoCarga.total}</p>
                            <p className="text-sm text-gray-500">Total procesados</p>
                          </div>
                          <div className="bg-emerald-50 rounded-lg p-4 text-center">
                            <p className="text-2xl font-bold text-emerald-600">{resultadoCarga.exitosos}</p>
                            <p className="text-sm text-emerald-600">Exitosos</p>
                          </div>
                          <div className="bg-red-50 rounded-lg p-4 text-center">
                            <p className="text-2xl font-bold text-red-600">{resultadoCarga.errores?.length || 0}</p>
                            <p className="text-sm text-red-600">Errores</p>
                          </div>
                        </div>

                        {/* Errors */}
                        {resultadoCarga.errores?.length > 0 && (
                          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                            <h4 className="font-semibold text-red-700 mb-2">Errores encontrados:</h4>
                            <ul className="space-y-1 text-sm">
                              {resultadoCarga.errores.map((err, i) => (
                                <li key={i} className="text-red-600">
                                  <span className="font-medium">Fila {err.fila}:</span> {err.error}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {resultadoCarga.exitosos > 0 && (
                          <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-emerald-700">
                            <Check className="w-4 h-4" />
                            Se cargaron {resultadoCarga.exitosos} trabajadores correctamente
                          </div>
                        )}
                      </>
                    )}

                    <Button variant="outline" onClick={resetCargaMasiva} className="w-full">
                      Cargar otro archivo
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Tab BAJAS / SUSPENDIDOS */}
        <TabsContent value="bajas">
          <BajasSuspendidosTab />
        </TabsContent>
      </Tabs>

      {/* View/Edit Modal */}
      <Dialog open={!!selectedTrabajador} onOpenChange={() => { setSelectedTrabajador(null); setEditForm(null); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {viewMode === 'view' ? 'Datos del Trabajador/a' : 'Editar Trabajador/a'}
            </DialogTitle>
          </DialogHeader>

          {viewMode === 'view' && selectedTrabajador && (
            <div className="space-y-6">
              {/* Basic Info */}
              <div>
                <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                  <User className="w-5 h-5 text-primary" />
                  Datos Básicos
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                  <div><span className="text-gray-500">Matrícula:</span> <span className="font-medium">{selectedTrabajador.numero_matricula}</span></div>
                  <div><span className="text-gray-500">Documento:</span> <span className="font-medium">{selectedTrabajador.documento}</span></div>
                  <div><span className="text-gray-500">Apellido:</span> <span className="font-medium">{selectedTrabajador.apellido}</span></div>
                  <div><span className="text-gray-500">Nombre:</span> <span className="font-medium">{selectedTrabajador.nombre}</span></div>
                  <div><span className="text-gray-500">Fecha Nac.:</span> <span className="font-medium">{selectedTrabajador.fecha_nacimiento}</span></div>
                  <div><span className="text-gray-500">Género:</span> <span className="font-medium">{selectedTrabajador.genero || '-'}</span></div>
                  <div><span className="text-gray-500">Título:</span> <span className="font-medium">{selectedTrabajador.titulo}</span></div>
                  <div><span className="text-gray-500">Email:</span> <span className="font-medium">{selectedTrabajador.email}</span></div>
                  <div><span className="text-gray-500">Celular:</span> <span className="font-medium">{selectedTrabajador.celular}</span></div>
                </div>
              </div>

              {/* Domicilio Real */}
              <div>
                <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-primary" />
                  Domicilio Real
                </h3>
                {selectedTrabajador.domicilio_real ? (
                  <p className="text-sm">
                    {selectedTrabajador.domicilio_real.calle} {selectedTrabajador.domicilio_real.numero}
                    {selectedTrabajador.domicilio_real.piso && `, Piso ${selectedTrabajador.domicilio_real.piso}`}
                    {selectedTrabajador.domicilio_real.depto && ` Depto ${selectedTrabajador.domicilio_real.depto}`}
                    {selectedTrabajador.domicilio_real.localidad && `, ${selectedTrabajador.domicilio_real.localidad}`}
                  </p>
                ) : <p className="text-sm text-gray-400">No especificado</p>}
              </div>

              {/* Domicilio Laboral */}
              <div>
                <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-primary" />
                  Domicilio Laboral
                </h3>
                {selectedTrabajador.domicilio_laboral ? (
                  <p className="text-sm">
                    {selectedTrabajador.domicilio_laboral.calle} {selectedTrabajador.domicilio_laboral.numero}
                    {selectedTrabajador.domicilio_laboral.piso && `, Piso ${selectedTrabajador.domicilio_laboral.piso}`}
                    {selectedTrabajador.domicilio_laboral.depto && ` Depto ${selectedTrabajador.domicilio_laboral.depto}`}
                    {selectedTrabajador.domicilio_laboral.localidad && `, ${selectedTrabajador.domicilio_laboral.localidad}`}
                  </p>
                ) : <p className="text-sm text-gray-400">No especificado</p>}
              </div>

              <ActualizacionFamiliarPerfil trabajadorId={selectedTrabajador.id} />

              {/* Historial de correos vinculados */}
              <div>
                <HistorialCorreosMatriculado trabajadorId={selectedTrabajador.id} />
              </div>

              {/* iter205 · Historial Biblioteca "Laura Iglesias" */}
              <div>
                <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-amber-600" />
                  Historial Biblioteca "Laura Iglesias"
                </h3>
                <HistorialBibliotecaMatriculada numeroMatricula={selectedTrabajador.numero_matricula} />
              </div>

              <DialogFooter className="flex-col sm:flex-row gap-2">
                <Button
                  variant="outline"
                  className="text-red-600 border-red-300 hover:bg-red-50 hover:text-red-700 w-full sm:w-auto"
                  onClick={() => setSuspenderModal({ open: true, trabajador: selectedTrabajador, motivo: '' })}
                  data-testid="btn-suspender-matriculado"
                >
                  <UserX className="w-4 h-4 mr-2" />
                  Suspender / Dar de baja
                </Button>
                <Button onClick={() => handleEdit(selectedTrabajador)} className="w-full sm:w-auto">
                  <Edit className="w-4 h-4 mr-2" />
                  Editar Datos
                </Button>
              </DialogFooter>
            </div>
          )}

          {viewMode === 'edit' && editForm && (
            <div className="space-y-6">
              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  <AlertCircle className="w-4 h-4" />
                  {error}
                </div>
              )}

              {/* Basic Info */}
              <div>
                <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                  <User className="w-5 h-5 text-primary" />
                  Datos Básicos
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Matrícula</Label>
                    <Input value={editForm.numero_matricula} readOnly className="bg-gray-50" />
                  </div>
                  <div className="space-y-2">
                    <Label>Documento</Label>
                    <Input value={editForm.documento} readOnly className="bg-gray-50" />
                  </div>
                  <div className="space-y-2">
                    <Label>Apellido</Label>
                    <Input value={editForm.apellido} onChange={(e) => updateEditForm('apellido', e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label>Nombre</Label>
                    <Input value={editForm.nombre} onChange={(e) => updateEditForm('nombre', e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label>Fecha Nacimiento</Label>
                    <Input type="date" data-testid="edit-fecha-nacimiento" value={editForm.fecha_nacimiento} onChange={(e) => updateEditForm('fecha_nacimiento', e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label>Género</Label>
                    <Select value={editForm.genero} onValueChange={(v) => updateEditForm('genero', v)}>
                      <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                      <SelectContent>
                        {GENEROS.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Título</Label>
                    <select 
                      value={editForm.titulo || ''} 
                      onChange={(e) => updateEditForm('titulo', e.target.value)}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    >
                      <option value="">Seleccionar</option>
                      <optgroup label="Titulaciones vigentes">
                        {TITULOS_VIGENTES.map(t => <option key={t} value={t}>{t}</option>)}
                      </optgroup>
                      <optgroup label="Titulaciones anteriores">
                        {TITULOS_ANTERIORES.map(t => <option key={t} value={t}>{t}</option>)}
                      </optgroup>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input type="email" value={editForm.email} onChange={(e) => updateEditForm('email', e.target.value)} />
                  </div>
                  <div className="space-y-2 col-span-2">
                    <Label>Celular</Label>
                    <Input value={editForm.celular} onChange={(e) => updateEditForm('celular', e.target.value)} />
                  </div>
                </div>
              </div>

              {/* Domicilio Real */}
              <div>
                <h3 className="text-lg font-semibold mb-3">Domicilio Real</h3>
                <div className="grid grid-cols-6 gap-4">
                  <div className="col-span-2 space-y-2">
                    <Label>Calle</Label>
                    <Input value={editForm.domicilio_real?.calle || ''} onChange={(e) => updateEditDomicilio('domicilio_real', 'calle', e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label>Número</Label>
                    <Input value={editForm.domicilio_real?.numero || ''} onChange={(e) => updateEditDomicilio('domicilio_real', 'numero', e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label>Piso</Label>
                    <Input value={editForm.domicilio_real?.piso || ''} onChange={(e) => updateEditDomicilio('domicilio_real', 'piso', e.target.value)} placeholder="Ej: 3" />
                  </div>
                  <div className="space-y-2">
                    <Label>Depto</Label>
                    <Input value={editForm.domicilio_real?.depto || ''} onChange={(e) => updateEditDomicilio('domicilio_real', 'depto', e.target.value)} placeholder="Ej: A" />
                  </div>
                  <div className="space-y-2">
                    <Label>Localidad</Label>
                    <Input value={editForm.domicilio_real?.localidad || ''} onChange={(e) => updateEditDomicilio('domicilio_real', 'localidad', e.target.value)} />
                  </div>
                </div>
              </div>

              {/* Domicilio Laboral */}
              <div>
                <h3 className="text-lg font-semibold mb-3">Domicilio Laboral</h3>
                <div className="grid grid-cols-6 gap-4">
                  <div className="col-span-2 space-y-2">
                    <Label>Calle</Label>
                    <Input value={editForm.domicilio_laboral?.calle || ''} onChange={(e) => updateEditDomicilio('domicilio_laboral', 'calle', e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label>Número</Label>
                    <Input value={editForm.domicilio_laboral?.numero || ''} onChange={(e) => updateEditDomicilio('domicilio_laboral', 'numero', e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label>Piso</Label>
                    <Input value={editForm.domicilio_laboral?.piso || ''} onChange={(e) => updateEditDomicilio('domicilio_laboral', 'piso', e.target.value)} placeholder="Ej: 3" />
                  </div>
                  <div className="space-y-2">
                    <Label>Depto</Label>
                    <Input value={editForm.domicilio_laboral?.depto || ''} onChange={(e) => updateEditDomicilio('domicilio_laboral', 'depto', e.target.value)} placeholder="Ej: A" />
                  </div>
                  <div className="space-y-2">
                    <Label>Localidad</Label>
                    <Input value={editForm.domicilio_laboral?.localidad || ''} onChange={(e) => updateEditDomicilio('domicilio_laboral', 'localidad', e.target.value)} />
                  </div>
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => { setSelectedTrabajador(null); setEditForm(null); }}>
                  Cancelar
                </Button>
                <Button onClick={handleSaveEdit} disabled={saving}>
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Guardando...' : 'Guardar Cambios'}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar Eliminación</DialogTitle>
          </DialogHeader>
          <p className="text-gray-500">
            ¿Estás seguro que deseas eliminar a <strong>{deleteConfirm?.nombre} {deleteConfirm?.apellido}</strong>?
            Esta acción no se puede deshacer.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={() => handleDelete(deleteConfirm.id)} data-testid="confirm-delete-btn">
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de baja manual del matriculado */}
      <Dialog
        open={suspenderModal.open}
        onOpenChange={(open) => !open && setSuspenderModal({ open: false, trabajador: null, motivo: '' })}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-700 flex items-center gap-2">
              <UserX className="w-5 h-5" />
              Dar de baja / Suspender matriculade
            </DialogTitle>
          </DialogHeader>
          {suspenderModal.trabajador && (
            <div className="space-y-4">
              <div className="p-3 bg-slate-50 rounded border text-sm">
                <div><strong>Matrícula:</strong> {suspenderModal.trabajador.numero_matricula}</div>
                <div><strong>Titular:</strong> {suspenderModal.trabajador.apellido}, {suspenderModal.trabajador.nombre}</div>
                <div><strong>Email:</strong> {suspenderModal.trabajador.email || '-'}</div>
              </div>
              <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">
                Esta acción lo mueve a la pestaña <strong>Bajas / Suspendidos</strong>, desactiva su usuario del portal
                y lo excluye del mailing de difusión y del envío del día 10. Podés revertirlo cuando quieras
                con el botón "Reactivar".
              </div>
              <div>
                <Label htmlFor="motivo-baja">Motivo de la baja *</Label>
                <textarea
                  id="motivo-baja"
                  className="w-full mt-1 border rounded p-2 text-sm min-h-[80px]"
                  placeholder="Ej: Fallecimiento / Renuncia voluntaria / Baja por sanción / Inhabilitado por Consejo…"
                  value={suspenderModal.motivo}
                  onChange={(e) => setSuspenderModal((s) => ({ ...s, motivo: e.target.value }))}
                  data-testid="input-motivo-baja"
                />
              </div>
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              className="w-full sm:w-auto"
              onClick={() => setSuspenderModal({ open: false, trabajador: null, motivo: '' })}
            >
              Cancelar
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white w-full sm:w-auto"
              onClick={handleSuspender}
              disabled={suspendiendo || !suspenderModal.motivo || suspenderModal.motivo.trim().length < 3}
              data-testid="btn-confirmar-baja-manual"
            >
              {suspendiendo ? 'Procesando…' : 'Confirmar baja'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ==================== INTERACCIONES WHATSAPP TAB ====================
function InteraccionesWhatsAppTab({ token }) {
  const [estadisticas, setEstadisticas] = useState(null);
  const [matriculados, setMatriculados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState('');
  const [enviandoInvitacion, setEnviandoInvitacion] = useState(null);

  useEffect(() => {
    cargarDatos();
  }, []);

  const cargarDatos = async () => {
    setLoading(true);
    try {
      const [statsRes, matriculadosRes] = await Promise.all([
        axios.get(`${API_URL}/webhooks/twilio/admin/estadisticas-interaccion-whatsapp`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/webhooks/twilio/admin/matriculados-whatsapp-sin-usuario`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setEstadisticas(statsRes.data);
      setMatriculados(matriculadosRes.data.matriculados || []);
    } catch (error) {
      console.error('Error cargando datos:', error);
    } finally {
      setLoading(false);
    }
  };

  const enviarInvitacion = async (trabajadorId, email) => {
    if (!email) {
      alert('Este matriculado no tiene email registrado');
      return;
    }
    
    setEnviandoInvitacion(trabajadorId);
    try {
      await axios.post(
        `${API_URL}/webhooks/twilio/admin/enviar-invitacion-usuario/${trabajadorId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert(`Invitación enviada a ${email}`);
      cargarDatos(); // Recargar para actualizar estado
    } catch (error) {
      console.error('Error enviando invitación:', error);
      alert(error.response?.data?.detail || 'Error al enviar la invitación');
    } finally {
      setEnviandoInvitacion(null);
    }
  };

  const matriculadosFiltrados = matriculados.filter(m => {
    if (!filtro) return true;
    const busqueda = filtro.toLowerCase();
    return (
      m.nombre?.toLowerCase().includes(busqueda) ||
      m.numero_matricula?.includes(busqueda) ||
      m.celular?.includes(busqueda) ||
      m.email?.toLowerCase().includes(busqueda)
    );
  });

  const formatearFecha = (fecha) => {
    if (!fecha) return '-';
    try {
      return new Date(fecha).toLocaleString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return fecha;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="w-6 h-6 animate-spin text-violet-600" />
        <span className="ml-2">Cargando datos...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Estadísticas */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-violet-100 rounded-lg">
                <Users className="w-5 h-5 text-violet-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{estadisticas?.total_matriculados || 0}</p>
                <p className="text-xs text-gray-500">Total Matriculados</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <UserCheck className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{estadisticas?.verificados_via_whatsapp || 0}</p>
                <p className="text-xs text-gray-500">Verificaron vía WhatsApp</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <MessageSquare className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{estadisticas?.actualizaron_via_whatsapp || 0}</p>
                <p className="text-xs text-gray-500">Actualizaron datos</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <AlertCircle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-amber-600">{estadisticas?.interactuaron_sin_usuario || 0}</p>
                <p className="text-xs text-gray-500">Sin usuario creado</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Porcentajes */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-around">
            <div className="text-center">
              <p className="text-3xl font-bold text-violet-600">{estadisticas?.porcentaje_verificados || 0}%</p>
              <p className="text-sm text-gray-500">Verificaron vía WhatsApp</p>
            </div>
            <div className="h-12 w-px bg-gray-200"></div>
            <div className="text-center">
              <p className="text-3xl font-bold text-green-600">{estadisticas?.porcentaje_con_usuario || 0}%</p>
              <p className="text-sm text-gray-500">Tienen usuario creado</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Lista de matriculados sin usuario */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-amber-500" />
                Matriculados que interactuaron pero no tienen usuario
              </CardTitle>
              <CardDescription>
                Personas que verificaron o actualizaron datos vía WhatsApp pero no completaron la creación de usuario
              </CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={cargarDatos}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Actualizar
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Buscador */}
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Buscar por nombre, matrícula, celular o email..."
                value={filtro}
                onChange={(e) => setFiltro(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {matriculadosFiltrados.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-500" />
              <p>No hay matriculados pendientes de crear usuario</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left p-3">Nombre</th>
                    <th className="text-left p-3">Matrícula</th>
                    <th className="text-left p-3">Celular</th>
                    <th className="text-left p-3">Email</th>
                    <th className="text-left p-3">Última interacción</th>
                    <th className="text-left p-3">Estado</th>
                    <th className="text-left p-3">Invitación</th>
                  </tr>
                </thead>
                <tbody>
                  {matriculadosFiltrados.map((m, idx) => (
                    <tr key={m.id || idx} className="border-b hover:bg-gray-50">
                      <td className="p-3 font-medium">{m.nombre || '-'}</td>
                      <td className="p-3">{m.numero_matricula || '-'}</td>
                      <td className="p-3">
                        {m.celular ? (
                          <a 
                            href={`https://wa.me/${m.celular.replace(/\D/g, '')}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-green-600 hover:underline flex items-center gap-1"
                          >
                            <MessageSquare className="w-3 h-3" />
                            {m.celular}
                          </a>
                        ) : '-'}
                      </td>
                      <td className="p-3 text-gray-600">{m.email || '-'}</td>
                      <td className="p-3 text-gray-500 text-xs">
                        {formatearFecha(m.ultima_actualizacion_whatsapp || m.ultimo_login_whatsapp)}
                      </td>
                      <td className="p-3">
                        <div className="flex gap-1">
                          {m.verificado_via_whatsapp && (
                            <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                              Verificó
                            </Badge>
                          )}
                          {m.actualizado_via_whatsapp && (
                            <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                              Actualizó
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="p-3">
                        {m.invitacion_usuario_enviada ? (
                          <div className="flex flex-col gap-1">
                            <Badge variant="outline" className="text-xs bg-violet-50 text-violet-700 border-violet-200">
                              <Mail className="w-3 h-3 mr-1" />
                              Enviada
                            </Badge>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 text-xs text-gray-500 hover:text-violet-600"
                              onClick={() => enviarInvitacion(m.id, m.email)}
                              disabled={enviandoInvitacion === m.id || !m.email}
                            >
                              {enviandoInvitacion === m.id ? (
                                <RefreshCw className="w-3 h-3 animate-spin" />
                              ) : (
                                'Reenviar'
                              )}
                            </Button>
                          </div>
                        ) : m.email ? (
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 text-xs bg-violet-50 text-violet-700 border-violet-200 hover:bg-violet-100"
                            onClick={() => enviarInvitacion(m.id, m.email)}
                            disabled={enviandoInvitacion === m.id}
                          >
                            {enviandoInvitacion === m.id ? (
                              <RefreshCw className="w-3 h-3 animate-spin mr-1" />
                            ) : (
                              <Mail className="w-3 h-3 mr-1" />
                            )}
                            Enviar
                          </Button>
                        ) : (
                          <span className="text-xs text-gray-400">Sin email</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          
          <div className="mt-4 text-sm text-gray-500">
            Mostrando {matriculadosFiltrados.length} de {matriculados.length} matriculados
          </div>
        </CardContent>
      </Card>
    </div>
  );
}


// ==========================================================================
// TAB: Bajas / Suspendidos
// ==========================================================================
function BajasSuspendidosTab() {
  const { token } = useAuth();
  const [bajas, setBajas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState('todas'); // todas | baja_detectada | baja_manual | reactivada_pendiente_confirmacion
  const [reactivandoId, setReactivandoId] = useState(null);
  const [historialAbierto, setHistorialAbierto] = useState(null);
  const [auditRun, setAuditRun] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [erroresAbiertos, setErroresAbiertos] = useState(false);

  const cargar = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API_URL}/admin/matriculades/bajas`, {
        params: { tipo: filtro },
        headers: { Authorization: `Bearer ${token}` },
      });
      setBajas(data.bajas || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const cargarAuditoria = async () => {
    try {
      const { data } = await axios.get(
        `${API_URL}/admin/matriculades/auditar-bajas-usuarios/estado`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setAuditRun(data.run || null);
    } catch (e) { /* silent */ }
  };

  useEffect(() => { cargar(); /* eslint-disable-next-line */ }, [filtro]);
  useEffect(() => { cargarAuditoria(); }, []); // eslint-disable-line

  // Polling mientras hay auditoría en curso
  useEffect(() => {
    if (!auditRun || (auditRun.estado !== 'iniciando' && auditRun.estado !== 'en_curso')) return;
    const t = setInterval(async () => {
      await cargarAuditoria();
    }, 3000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [auditRun?.estado]);

  // Al finalizar auditoría, refresco la lista de bajas
  useEffect(() => {
    if (auditRun?.estado === 'completado') cargar();
    // eslint-disable-next-line
  }, [auditRun?.estado]);

  const dispararAuditoria = async () => {
    if (!window.confirm('¿Ejecutar control ahora contra ANTUAR?\n\nSe consultarán todos los usuarios del portal vinculados al padrón. Puede demorar varios minutos.')) return;
    try {
      setAuditLoading(true);
      await axios.post(
        `${API_URL}/admin/matriculades/auditar-bajas-usuarios`,
        { solo_con_usuario_activo: true },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      await cargarAuditoria();
    } catch (e) {
      alert('Error: ' + (e.response?.data?.detail || e.message));
    } finally {
      setAuditLoading(false);
    }
  };

  const reactivar = async (b) => {
    if (!window.confirm(`¿Reactivar a ${b.nombre} ${b.apellido} (mat ${b.numero_matricula})?\n\nEsto la vuelve a la lista de activos y reactiva su usuario del portal.`)) return;
    try {
      setReactivandoId(b.id);
      await axios.post(
        `${API_URL}/admin/matriculades/${b.id}/reactivar`,
        { motivo: 'Reactivación manual desde panel Bajas' },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      await cargar();
    } catch (e) {
      alert('Error: ' + (e.response?.data?.detail || e.message));
    } finally {
      setReactivandoId(null);
    }
  };

  const estadoBadge = (estado) => {
    const meta = {
      baja_detectada: { color: 'bg-red-100 text-red-800', label: 'Detectada por ANTUAR' },
      baja_manual: { color: 'bg-slate-200 text-slate-800', label: 'Baja manual' },
      reactivada_pendiente_confirmacion: { color: 'bg-amber-100 text-amber-800', label: 'Volvió activa — confirmar' },
    }[estado] || { color: 'bg-gray-100 text-gray-700', label: estado };
    return <Badge className={`${meta.color} text-xs`}>{meta.label}</Badge>;
  };

  const pendientesConfirmacion = bajas.filter(b => b.estado_padron === 'reactivada_pendiente_confirmacion');

  return (
    <div className="space-y-4">
      {/* Control manual de auditoría */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="pt-4 pb-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex-1">
              <p className="font-semibold text-blue-900 flex items-center gap-2">
                <RefreshCw className="w-4 h-4" />
                Control manual contra ANTUAR
              </p>
              <p className="text-sm text-blue-800">
                Corré una auditoría ahora mismo sobre todos los usuarios del portal vinculados al padrón.
                No hace falta esperar al ciclo del día 10.
              </p>
              {auditRun && (
                <div className="mt-2 text-xs text-blue-900">
                  {auditRun.estado === 'iniciando' && <>Iniciando…</>}
                  {auditRun.estado === 'en_curso' && (
                    <>En curso: <strong>{auditRun.procesados}/{auditRun.total}</strong> procesados · <strong>{auditRun.bajas_detectadas}</strong> bajas detectadas · <button
                      type="button"
                      onClick={() => (auditRun.errores > 0) && setErroresAbiertos(true)}
                      className={`${auditRun.errores > 0 ? 'underline text-blue-700 hover:text-blue-900 cursor-pointer' : 'text-blue-900'}`}
                      data-testid="btn-errores-auditoria"
                    >{auditRun.errores} errores</button></>
                  )}
                  {auditRun.estado === 'completado' && (
                    <>Último control: <strong>{auditRun.procesados}</strong> revisados · <strong>{auditRun.bajas_detectadas}</strong> bajas detectadas · <button
                      type="button"
                      onClick={() => (auditRun.errores > 0) && setErroresAbiertos(true)}
                      className={`${auditRun.errores > 0 ? 'underline text-blue-700 hover:text-blue-900 cursor-pointer' : 'text-blue-900'}`}
                      data-testid="btn-errores-auditoria"
                    >{auditRun.errores} errores</button> · terminó {new Date(auditRun.finalizado_at).toLocaleString('es-AR')}</>
                  )}
                </div>
              )}
            </div>
            <Button
              onClick={dispararAuditoria}
              disabled={auditLoading || (auditRun && ['iniciando', 'en_curso'].includes(auditRun.estado))}
              className="bg-blue-600 hover:bg-blue-700 text-white w-full sm:w-auto"
              data-testid="btn-ejecutar-auditoria-bajas"
            >
              {auditLoading || (auditRun && ['iniciando', 'en_curso'].includes(auditRun.estado)) ? (
                <><RefreshCw className="w-4 h-4 mr-1 animate-spin" /> Auditando…</>
              ) : (
                <><RefreshCw className="w-4 h-4 mr-1" /> Ejecutar control ahora</>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Aviso reactivaciones pendientes */}
      {pendientesConfirmacion.length > 0 && (
        <Card className="border-amber-300 bg-amber-50">
          <CardContent className="pt-4 pb-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-semibold text-amber-900">
                {pendientesConfirmacion.length} matrícula(s) volvieron a aparecer como activas en ANTUAR
              </p>
              <p className="text-sm text-amber-800">
                Confirmá manualmente su reactivación con el botón "Reactivar" para que vuelvan a la lista de activos.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2">
                <UserX className="w-5 h-5 text-red-600" />
                Bajas y Suspensiones
              </CardTitle>
              <CardDescription>
                Socios detectados como baja en ANTUAR o marcados manualmente. Sus usuarios del portal
                están desactivados y no reciben mailing de difusión ni el envío del día 10.
              </CardDescription>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {[
                { id: 'todas', label: 'Todas' },
                { id: 'baja_detectada', label: 'Detectadas por ANTUAR' },
                { id: 'baja_manual', label: 'Manuales' },
                { id: 'reactivada_pendiente_confirmacion', label: 'Pendientes de confirmar' },
              ].map(f => (
                <button
                  key={f.id}
                  onClick={() => setFiltro(f.id)}
                  className={`px-3 py-1 rounded text-xs whitespace-nowrap ${
                    filtro === f.id ? 'bg-red-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                  data-testid={`filtro-bajas-${f.id}`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-gray-500">Cargando…</div>
          ) : bajas.length === 0 ? (
            <div className="text-center py-8 text-gray-500" data-testid="empty-bajas">
              No hay matrículas en este estado.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left">
                  <tr>
                    <th className="py-2 px-3">Matrícula</th>
                    <th className="py-2 px-3">Nombre</th>
                    <th className="py-2 px-3">Estado</th>
                    <th className="py-2 px-3">Detectada</th>
                    <th className="py-2 px-3">Motivo</th>
                    <th className="py-2 px-3 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {bajas.map(b => (
                    <tr key={b.id} className="border-t hover:bg-slate-50" data-testid={`row-baja-${b.id}`}>
                      <td className="py-2 px-3 font-mono">{b.numero_matricula || '-'}</td>
                      <td className="py-2 px-3">{b.apellido}, {b.nombre}</td>
                      <td className="py-2 px-3">{estadoBadge(b.estado_padron)}</td>
                      <td className="py-2 px-3 text-slate-500 text-xs">
                        {b.fecha_baja_detectada ? new Date(b.fecha_baja_detectada).toLocaleDateString('es-AR') : '-'}
                      </td>
                      <td className="py-2 px-3 text-xs text-slate-600 max-w-xs truncate" title={b.motivo_baja || ''}>
                        {b.motivo_baja || '-'}
                      </td>
                      <td className="py-2 px-3 text-right">
                        <div className="inline-flex gap-1">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setHistorialAbierto(b)}
                            data-testid={`btn-historial-${b.id}`}
                          >
                            <FileText className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700 text-white"
                            onClick={() => reactivar(b)}
                            disabled={reactivandoId === b.id}
                            data-testid={`btn-reactivar-${b.id}`}
                          >
                            <RotateCcw className="w-4 h-4 mr-1" />
                            Reactivar
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal Historial */}
      <Dialog open={!!historialAbierto} onOpenChange={() => setHistorialAbierto(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Historial de baja — {historialAbierto?.apellido}, {historialAbierto?.nombre}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 text-sm">
            {(historialAbierto?.historial_estado_padron || []).map((h, i) => (
              <div key={i} className="border-l-2 border-slate-300 pl-3 pb-2">
                <div className="text-xs text-slate-500">
                  {new Date(h.fecha).toLocaleString('es-AR')} — <strong>{h.accion}</strong>
                </div>
                <div>{h.detalle}</div>
                {h.por && <div className="text-xs text-slate-500">por {h.por}</div>}
              </div>
            ))}
            {(!historialAbierto?.historial_estado_padron || historialAbierto.historial_estado_padron.length === 0) && (
              <div className="text-slate-500">Sin historial registrado.</div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Errores de auditoría */}
      <Dialog open={erroresAbiertos} onOpenChange={setErroresAbiertos}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <AlertCircle className="w-5 h-5" />
              Socios que no pudieron ser auditados ({auditRun?.errores || 0})
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto text-sm">
            {(auditRun?.errores_detalle || []).length === 0 ? (
              <div className="text-slate-500 text-center py-6">Sin detalles disponibles.</div>
            ) : (
              (auditRun.errores_detalle || []).map((e, i) => (
                <div key={i} className="border rounded p-2 bg-amber-50 border-amber-200" data-testid={`error-row-${i}`}>
                  <div className="flex flex-wrap justify-between gap-2">
                    <span className="font-mono text-xs bg-white border px-1.5 rounded">Mat {e.matricula}</span>
                    <span className="text-slate-700 font-medium">{e.nombre}</span>
                  </div>
                  <div className="text-xs text-amber-900 mt-1">{e.motivo}</div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
